import { ElementType, ReactNode } from 'react'
import { ExternalLink } from '@stratascratch/icons'
import QuestionTitle from './QuestionTitle'
import QuestionDifficulty from './QuestionDifficulty'
import QuestionType from './QuestionType'
import Markdown from './Markdown'
import './QuestionMetadata.css'

interface Props {
  title?: string | null
  titleTag?: ElementType
  url?: string
  titleAddons?: ReactNode
  showLock: boolean
  date: Date | undefined
  companyNames: (string | undefined)[] | undefined
  jobPositions: (string | undefined)[] | undefined
  difficulty?: number | null
  questionType?: string | null
  visualizationType?: string | null
  analyticalObjective?: string | null
  figSizeMatplotlib?: string | null
  datasetType?: string | null
  solutionMatplotlibColorset?: string | null
  solutionPlotlyColorset?: string | null
  id: string
  metadataAddons?: ReactNode
  question?: string | null
}

function QuestionMetadata({
  title = '',
  titleTag: TitleTag = 'h1',
  url = undefined,
  titleAddons = undefined,
  showLock,
  id,
  date,
  companyNames,
  jobPositions,
  datasetType = undefined,
  solutionMatplotlibColorset = undefined,
  solutionPlotlyColorset = undefined,
  figSizeMatplotlib = undefined,
  visualizationType = undefined,
  analyticalObjective = undefined,
  difficulty = undefined,
  questionType = undefined,
  metadataAddons = undefined,
  question = '',
}: Props) {
  if (!title) {
    return <>Missing question data</>
  }

  return (
    <>
      <div className="flex items-center gap-4">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            aria-label="Open Question in new tab"
            className="text-(--color-link)"
          >
            <TitleTag className="QuestionMetadata__h1 my-0 items-center gap-4 text-2xl">
              <QuestionTitle title={title} showLock={showLock} />
              <ExternalLink className="mb-1 ms-2 inline text-lg" />
            </TitleTag>
          </a>
        ) : (
          <TitleTag className="QuestionMetadata__h1 my-0 flex flex-grow items-center gap-4 text-3xl">
            <QuestionTitle title={title} showLock={showLock} />
          </TitleTag>
        )}
        {titleAddons}
      </div>
      {date && (
        <p className="QuestionMetadata__date">
          Last Updated:{' '}
          {date.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          })}
        </p>
      )}
      <div className="QuestionMetadata__metadata my-4">
        {companyNames &&
          companyNames.map((name) => <div key={name}>{name}</div>)}
        {difficulty && <QuestionDifficulty difficulty={difficulty} />}
        {questionType && <QuestionType questionType={questionType} />}
        <span>ID {id}</span>
        {visualizationType && <span>{visualizationType}</span>}
        {analyticalObjective && <span>{analyticalObjective}</span>}
        {datasetType && <span>{datasetType}</span>}
        {metadataAddons}
      </div>
      <div className="QuestionMetadata__metadata my-4">
        {jobPositions &&
          jobPositions.map((name) => <div key={name}>{name}</div>)}
      </div>
      <hr className="my-4" />
      <Markdown className="QuestionMetadata__question text-sm">
        {question || ''}
      </Markdown>
      <div className="flex flex-wrap gap-2">
        {solutionMatplotlibColorset && (
          <div className="QuestionMetadata__pill">
            Matplotlib Color Mapping: {solutionMatplotlibColorset}
          </div>
        )}
        {figSizeMatplotlib && (
          <div className="QuestionMetadata__pill">
            Matplotlib Figure Size: {figSizeMatplotlib}
          </div>
        )}
        {solutionPlotlyColorset && (
          <div className="QuestionMetadata__pill">
            Plotly Color Mapping: {solutionPlotlyColorset}
          </div>
        )}
      </div>
    </>
  )
}

export default QuestionMetadata
